import { APP_CONFIG } from '../config/app.config';

// Content blocks for the conversion-focused homepage.
// Copy is original to Cates — do not paste in competitor marketing text.

// ─── Proof, not adjectives ───────────────────────────────────────────────
// `animate` is opt-in and deliberate: only quantities count up. A licence number
// contains digits but is an identifier, not a measurement — animating it looks
// broken and undermines the one figure on the page a homeowner may verify.
export const PROOF_POINTS = [
  { value: 'Certified', label: 'Owens Corning Preferred Contractor', icon: 'award' },
  { value: 'Family Owned', label: 'Locally Owned & Operated', icon: 'heart' },
  { value: 'In-House', label: 'Same Trusted Crews', icon: 'users' },
  { value: 'Specialists', label: 'Insurance Claim Handling', icon: 'file' },
  { value: APP_CONFIG.licenseNumber, label: 'FL Licensed, Bonded & Insured', icon: 'shield' },
];

// ─── Cates vs a typical roofing company ─────────────────────────────────
export const COMPARISON_ROWS = [
  { cates: 'Locally & family owned', typical: 'Large or non-local companies' },
  { cates: ' Experienced, reliable crews', typical: 'Crews may vary by project' },
  { cates: 'Owens Corning Preferred Contractor', typical: 'Manufacturer certification may vary' },
  { cates: 'Insurance claim guidance', typical: 'Homeowner may manage the claim alone' },
  { cates: 'Premium materials', typical: 'Material quality can vary' },
  { cates: 'Dedicated project manager', typical: 'Multiple points of contact' },
  { cates: '5-star Google rating', typical: 'Reputation varies by contractor' },
];

// ─── What the free inspection actually includes ─────────────────────────
export const INSPECTION_STEPS = [
  { title: 'Drone Inspection', text: 'Full aerial survey of every slope, ridge, and valley, including areas a ladder cannot safely reach.' },
  { title: 'Attic Inspection', text: 'Where access allows, we inspect the decking and insulation from underneath for signs of moisture, leaks, and hidden damage.' },
  { title: 'Wind & Hail Assessment', text: 'Lifted or missing shingles, creases, granule loss, damaged flashing, and other signs of wind and hail damage.' },
  { title: 'Full Photo Report', text: 'Every finding photographed and annotated, delivered to you whether or not you hire us.' },
  { title: 'Insurance Documentation', text: 'Detailed measurements, photos, and damage documentation organized for your insurance claim.' },
  { title: 'Honest Recommendations', text: ' If your roof has years of life left, we will tell you. We would rather lose a job than sell you a roof you do not need.' },
];

// ─── Why premium roofing is actually different ──────────────────────────
export const PREMIUM_EXPLAINED = [
  { title: 'Larger Nailing Zone', text: 'Owens Corning Duration shingles feature a reinforced nailing zone that gives installers a larger, more consistent fastening target. Proper nail placement helps shingles stay secure when the wind picks up.' },
  { title: 'Real Wind Resistance', text: 'Wind performance depends on more than the shingle itself. Proper fastener placement, nail count, starter shingles, and installation all work together to help the roofing system perform as designed in high winds.' },
  { title: 'Upgraded Underlayment', text: 'We use synthetic underlayment instead of traditional felt. It is stronger, more water resistant, and less prone to wrinkling, giving your roof another layer of protection beneath the shingles.' },
  { title: 'Balanced Ventilation', text: ' Proper attic ventilation requires both intake at the soffits and exhaust at the ridge. Getting that balance right helps control heat and moisture that can damage decking, reduce energy efficiency, and shorten the life of your roof.' },
  { title: 'Flashing Done Properly', text: 'Chimneys, walls, valleys, and roof penetrations are some of the most leak prone areas on a roof. We replace flashing where needed instead of relying on old metal and sealant to protect a brand new roofing system.' },
  { title: 'Workmanship That Is Inspected', text: 'A project manager inspects the installation details you may never see from the ground, then walks the completed roof with you before the job is closed out.' },
];

// ─── Owens Corning authority ────────────────────────────────────────────
export const OWENS_CORNING_POINTS = [
  { title: 'What Preferred Contractor Means', text: 'Preferred Contractor status is more than a badge. Contractors must meet specific requirements to qualify and maintain their standing in the Owens Corning Contractor Network. It gives homeowners another level of confidence in the company installing one of the most important systems on their home.' },
  { title: 'Why Certification Matters', text: 'A roof is more than the shingles you see. Underlayment, starter shingles, flashing, ventilation, fastening, and other components all work together as a complete roofing system. We install each component according to manufacturer specifications so the entire roof performs the way it was designed to.' },
  { title: 'Why It Matters to You', text: 'Premium shingles can only perform as well as the system underneath them and the workmanship behind them. Proper installation helps protect against leaks, premature failure, and other problems that may not appear until years later. It also helps ensure your roofing system meets the manufacturer requirements associated with its warranty.' },
];

// ─── The Cates difference ───────────────────────────────────────────────
export const CATES_DIFFERENCE = [
  { title: 'Experienced, Reliable Crews', text: ' Consistent crews, clear accountability, and workmanship you can depend on from start to finish.' },
  { title: 'Daily Communication', text: ' You know where your project stands each day, not just when there is a problem or an invoice.' },
  { title: 'Site Cleanup', text: 'We protect your property, sweep for nails and debris, and clean the jobsite throughout the project.' },
  { title: 'Dedicated Project Manager', text: ' One point of contact oversees your project from inspection through the final walkthrough.' },
  { title: 'Property Protection', text: ' We use the Catch-All System to protect landscaping and surrounding areas and contain debris before the first shingle comes off.' },
  { title: 'Quality Workmanship', text: ' Cut lines, valleys, flashing, and other critical details are finished with care, including the areas you may never see.' },
  { title: 'Manufacturer Standards', text: 'Your roofing system is installed according to manufacturer specifications for proper performance and applicable warranty requirements.' },
  { title: 'Final Walkthrough', text: 'We review the completed work with you, answer your questions, and provide photo documentation before the project is closed out.' },
];

// ─── Real urgency ───────────────────────────────────────────────────────
export const URGENCY_REASONS = [
  { title: 'Hurricane Season', text: ' Florida’s hurricane season runs from June through November. After a major storm, roofing schedules can fill quickly, making it harder to get repairs completed when you need them.' },
  { title: 'Insurance Claim Deadlines', text: ' Insurance policies and Florida law can limit how long you have to report certain types of storm damage. Waiting too long could affect your ability to make a claim.' },
  { title: 'Active Water Intrusion', text: ' A ceiling leak may mean water has already reached the decking, insulation, or other areas of your home. The longer it continues, the more extensive the damage can become.' },
  { title: 'Missing or Lifted Shingles', text: 'Missing or lifted shingles can expose the roofing system underneath to rain, wind, and UV exposure. A small repair today can become a much larger problem if left unaddressed.' },
  { title: 'Existing Leaks', text: ' Most insurance policies require homeowners to take reasonable steps to prevent further damage after a loss. If you know you have a leak, addressing it quickly helps protect your home and document that you took action.' },
];

// ─── Service area — Northeast Florida ───────────────────────────────────
// x/y are percentage coordinates on the map panel, not real geo-coordinates.
export const SERVICE_AREAS_MAP = [
  { city: 'Jacksonville', x: 42, y: 30, primary: true },
  { city: 'Jacksonville Beach', x: 74, y: 41 },
  { city: 'Atlantic Beach', x: 72, y: 22 },
  { city: 'Neptune Beach', x: 76, y: 31 },
  { city: 'Ponte Vedra', x: 70, y: 52 },
  { city: 'Nocatee', x: 63, y: 63 },
  { city: 'Orange Park', x: 32, y: 47 },
  { city: 'Fleming Island', x: 34, y: 57 },
  { city: 'Middleburg', x: 24, y: 62 },
  { city: 'Green Cove Springs', x: 33, y: 70 },
  { city: 'St. Augustine', x: 62, y: 80 },
  { city: 'Fernandina Beach', x: 63, y: 10 },
];

// ─── Google reviews ─────────────────────────────────────────────────────
// PLACEHOLDER REVIEWS — written to demonstrate the card format. Every one must be
// replaced with real Google review content before launch.
//
// IMPORTANT: `verified: true` renders the Google mark and verified tick on a card.
// Do NOT set it on invented content — badging fabricated reviews as Google-verified
// misrepresents them as third-party-attested and is an advertising-compliance
// problem, not a styling choice. It stays false until real reviews are wired in.
export interface GoogleReview {
  author: string;
  initial: string;
  avatarColor: string;
  // Empty until real reviews land — printing a stand-in string under each name
  // reads as a rendering bug rather than as placeholder data.
  relativeDate: string;
  rating: number;
  text: string;
  verified: boolean;
}

export const GOOGLE_REVIEWS: GoogleReview[] = [
  {
    author: 'The Harrison Family',
    initial: 'H',
    avatarColor: '#0072c6',
    relativeDate: '',
    rating: 5,
    text: 'Our carrier originally approved repairs only. Cates re-inspected, documented additional storm damage we had not spotted, met our adjuster on the roof, and the claim was reassessed as a full replacement. We would never have known to push back on our own.',
    verified: false,
  },
  {
    author: 'Michael & Dana R.',
    initial: 'M',
    avatarColor: '#1493e6',
    relativeDate: '',
    rating: 5,
    text: 'Three companies quoted. Cates was the only one who went into the attic and showed us photographs of what was actually happening up there. Same crew every day, same project manager on the phone, and the driveway was cleaner when they left than when they arrived.',
    verified: false,
  },
  {
    author: 'Sandra P.',
    initial: 'S',
    avatarColor: '#0f766e',
    relativeDate: '',
    rating: 5,
    text: 'They tarped our roof the evening we called, before anything else was agreed. That single decision is what stopped the water reaching the ceiling and, our adjuster told us later, is what kept the claim straightforward.',
    verified: false,
  },
  {
    author: 'Ray T.',
    initial: 'R',
    avatarColor: '#7c3aed',
    relativeDate: '',
    rating: 5,
    text: 'Straight answers on a roof I was told twice needed replacing. Cates inspected it, showed me the photos and said it had years left. They could have sold me a roof that day and did not.',
    verified: false,
  },
  {
    author: 'Elaine M.',
    initial: 'E',
    avatarColor: '#b45309',
    relativeDate: '',
    rating: 5,
    text: 'Tile repair after the last storm. Matched the existing tile so well I cannot pick out the section they worked on, and they walked the finished roof with me before invoicing.',
    verified: false,
  },
];

// ─── Headline stats ─────────────────────────────────────────────────────
// The v1 site's four-element band (Roofing-v1-code branch), restored on client
// request — v1's "Happy Customers" is stated as the Google review count instead,
// so the reputation figure appears once on the page rather than twice.
//
// Review count comes from APP_CONFIG so the site has a single source for it; the
// service-area count follows SERVICE_AREAS_MAP above rather than being retyped.
//
// Roofs Completed and the 100 years are inherited and still UNVERIFIED. Animating
// them makes them more prominent, so they need confirming before launch.
export const HEADLINE_STATS = [
  { value: '3,200+', label: 'Roofs Completed', icon: '🏠' },
  { value: APP_CONFIG.reputation.reviewCount, label: 'Google Reviews', icon: '⭐' },
  { value: '100', label: 'Years of Family Legacy', icon: '📅' },
  { value: `${SERVICE_AREAS_MAP.length}+`, label: 'Service Areas', icon: '📍' },
];
